import React, { useContext, useEffect, useState } from 'react';
import { Button, Checkbox, Form, Icon, Image, Modal } from 'semantic-ui-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/User';
import {
  API,
  getLogo,
  getSystemName,
  showError,
  showSuccess,
  showWarning,
} from '../helpers';
import { onGitHubOAuthClicked, onLarkOAuthClicked } from './utils';
import larkIcon from '../images/lark.svg';
import SinoTokenBackground from './SinoTokenBackground';
import './LoginForm.css';

const LoginForm = () => {
  const { t } = useTranslation();
  const [inputs, setInputs] = useState({
    username: '',
    password: '',
    wechat_verification_code: '',
  });
  const [searchParams] = useSearchParams();
  const { username, password } = inputs;
  const [, userDispatch] = useContext(UserContext);
  let navigate = useNavigate();
  const [status, setStatus] = useState({});
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const logo = getLogo();
  const systemName = getSystemName();
  const loginExpired = searchParams.get('expired');

  useEffect(() => {
    document.body.classList.add('sino-login-shell');
    if (loginExpired) {
      showError(t('messages.error.login_expired'));
    }
    let status = localStorage.getItem('status');
    if (status) {
      status = JSON.parse(status);
      setStatus(status);
    }
    return () => {
      document.body.classList.remove('sino-login-shell');
    };
  }, [loginExpired, t]);

  const [showWeChatLoginModal, setShowWeChatLoginModal] = useState(false);

  const onWeChatLoginClicked = () => {
    setShowWeChatLoginModal(true);
  };

  const onSubmitWeChatVerificationCode = async () => {
    const res = await API.get(
      `/api/oauth/wechat?code=${inputs.wechat_verification_code}`
    );
    const { success, message, data } = res.data;
    if (success) {
      userDispatch({ type: 'login', payload: data });
      localStorage.setItem('user', JSON.stringify(data));
      navigate('/dashboard');
      showSuccess(t('messages.success.login'));
      setShowWeChatLoginModal(false);
    } else {
      showError(message);
    }
  };

  function handleChange(e) {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  }

  async function handleSubmit(e) {
    if (e) {
      e.preventDefault();
    }
    if (username && password) {
      const res = await API.post(`/api/user/login`, {
        username,
        password,
      });
      const { success, message, data } = res.data;
      if (success) {
        userDispatch({ type: 'login', payload: data });
        localStorage.setItem('user', JSON.stringify(data));
        navigate('/dashboard');
        showSuccess(t('messages.success.login'));
        if (username === 'root' && password === '123456') {
          showWarning(t('messages.error.root_password'));
        }
      } else {
        showError(message);
      }
    }
  }

  const handleWechatButton = () => {
    if (status.wechat_login) {
      onWeChatLoginClicked();
      return;
    }
    showError('暂未开启微信登录');
  };

  return (
    <main className='sino-login-page'>
      <SinoTokenBackground />
      <section className='sino-login-art'>
        <header className='sino-login-brand'>
          <span className='sino-login-brand-mark'>
            <img src={logo} alt={systemName} />
          </span>
        </header>

        <nav className='sino-login-policy'>
          <Link to='/about'>隐私政策</Link>
          <Link to='/about'>服务条款</Link>
        </nav>
      </section>

      <section className='sino-login-panel'>
        <div className='sino-login-card'>
          <h1>欢迎回来！</h1>
          <p>请输入你的账号信息</p>

          <Form className='sino-login-form' onSubmit={handleSubmit}>
            <label htmlFor='login-username'>手机号</label>
            <input
              id='login-username'
              name='username'
              placeholder='13800138000'
              value={username}
              onChange={handleChange}
            />

            <label htmlFor='login-password'>密码</label>
            <div className='sino-password-field'>
              <input
                id='login-password'
                name='password'
                placeholder='••••••••'
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handleChange}
              />
              <button
                type='button'
                aria-label='显示或隐藏密码'
                onClick={() => setShowPassword((visible) => !visible)}
              >
                <Icon name={showPassword ? 'eye slash outline' : 'eye'} />
              </button>
            </div>

            <div className='sino-login-options'>
              <Checkbox
                label='30 天内保持登录'
                checked={rememberMe}
                onChange={(_, { checked }) => setRememberMe(checked)}
              />
              <Link to='/reset'>忘记密码？</Link>
            </div>

            <Button fluid type='submit' className='sino-login-submit'>
              登录
            </Button>
            <Button
              fluid
              type='button'
              className='sino-login-wechat'
              onClick={handleWechatButton}
            >
              使用微信登录
            </Button>
          </Form>

          <p className='sino-login-device'>当前没有待绑定设备。</p>

          {(status.github_oauth || status.lark_client_id) && (
            <div className='sino-login-oauth'>
              {status.github_oauth && (
                <Button
                  circular
                  color='black'
                  icon='github'
                  onClick={() => onGitHubOAuthClicked(status.github_client_id)}
                />
              )}
              {status.lark_client_id && (
                <button
                  type='button'
                  className='sino-lark-button'
                  onClick={() => onLarkOAuthClicked(status.lark_client_id)}
                >
                  <Image src={larkIcon} avatar />
                </button>
              )}
            </div>
          )}

          <p className='sino-login-register'>
            还没有账号？
            <Link to='/register'>立即注册</Link>
          </p>
        </div>
        <Modal
          onClose={() => setShowWeChatLoginModal(false)}
          onOpen={() => setShowWeChatLoginModal(true)}
          open={showWeChatLoginModal}
          size={'mini'}
        >
          <Modal.Content>
            <Modal.Description>
              <Image src={status.wechat_qrcode} fluid />
              <div style={{ textAlign: 'center' }}>
                <p>{t('auth.login.wechat.scan_tip')}</p>
              </div>
              <Form size='large'>
                <Form.Input
                  fluid
                  placeholder={t('auth.login.wechat.code_placeholder')}
                  name='wechat_verification_code'
                  value={inputs.wechat_verification_code}
                  onChange={handleChange}
                />
                <Button
                  fluid
                  size='large'
                  style={{
                    background: '#2F73FF',
                    color: 'white',
                    marginBottom: '1.5em',
                  }}
                  onClick={onSubmitWeChatVerificationCode}
                >
                  {t('auth.login.button')}
                </Button>
              </Form>
            </Modal.Description>
          </Modal.Content>
        </Modal>
      </section>
    </main>
  );
};

export default LoginForm;
